import { CaretDown, Check } from '@phosphor-icons/react';
import { Select as S } from 'radix-ui';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Mostra uma opção "vazia" no topo que devolve "" ao escolher. */
  clearLabel?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  'aria-label'?: string;
}

const EMPTY = '__empty__';

/** Select próprio (Radix), nunca o <select> nativo. Valores vazios viram "" para o formulário. */
export function Select({ value, onChange, options, placeholder = 'Selecione', clearLabel, className, ...rest }: SelectProps) {
  return (
    <S.Root value={value ? value : undefined} onValueChange={(v) => onChange(v === EMPTY ? '' : v)} disabled={rest.disabled} name={rest.name}>
      <S.Trigger
        id={rest.id}
        aria-invalid={rest['aria-invalid']}
        aria-describedby={rest['aria-describedby']}
        aria-label={rest['aria-label']}
        className={cn(
          'inline-flex h-10 w-full items-center justify-between gap-2 rounded-control border border-input bg-card px-3 text-left text-sm transition-colors hover:border-foreground/50 focus-visible:border-ring focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-70 aria-[invalid=true]:border-destructive data-[placeholder]:text-muted-foreground/80',
          className,
        )}
      >
        <span className="truncate">
          <S.Value placeholder={placeholder} />
        </span>
        <S.Icon>
          <CaretDown className="size-4 text-muted-foreground" aria-hidden />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className="oc-pop-in z-[70] max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-panel border bg-card text-card-foreground shadow-[0_12px_32px_-12px_oklch(0.25_0.04_205/0.35)]"
        >
          <S.Viewport className="p-1">
            {clearLabel && <Item value={EMPTY} label={clearLabel} muted />}
            {options.map((o) => (
              <Item key={o.value} value={o.value} label={o.label} disabled={o.disabled} />
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

function Item({ value, label, disabled, muted }: { value: string; label: string; disabled?: boolean; muted?: boolean }) {
  return (
    <S.Item
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex min-h-9 cursor-pointer items-center rounded-control py-2 pr-8 pl-3 text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-muted',
        muted && 'text-muted-foreground',
      )}
    >
      <S.ItemText>{label}</S.ItemText>
      <S.ItemIndicator className="absolute right-2.5">
        <Check className="size-4 text-primary" weight="bold" aria-hidden />
      </S.ItemIndicator>
    </S.Item>
  );
}
